import React from 'react';
import {FetchResult} from '@apollo/client';
import {NavigateFunction} from 'react-router-dom';
import {OptionsObject, SnackbarKey, SnackbarMessage} from 'notistack';
import {Button} from '@mui/material';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {BuilderDetails} from '../../../schema-types/BuilderDetails';
import {BuilderPot} from '../../../schema-types/BuilderPot';
import {AccountBasicFragment_contacts} from '../../../schema-types/AccountBasicFragment';
import {
  UpsertOvbuilder,
  UpsertOvbuilderVariables,
} from '../../../schema-types/UpsertOvbuilder';
import {
  UpsertSetUpNew,
  UpsertSetUpNewVariables,
} from '../../../schema-types/UpsertSetUpNew';
import {
  UpsertBuilderModify,
  UpsertBuilderModifyVariables,
} from '../../../schema-types/UpsertBuilderModify';
import {
  UpsertBuilderProtection,
  UpsertBuilderProtectionVariables,
} from '../../../schema-types/UpsertBuilderProtection';
import {
  UpsertBuilderAnnuity,
  UpsertBuilderAnnuityVariables,
} from '../../../schema-types/UpsertBuilderAnnuity';
import {
  UpsertBuilderContribute,
  UpsertBuilderContributeVariables,
} from '../../../schema-types/UpsertBuilderContribute';
import {
  UpsertBuilderWithdraw,
  UpsertBuilderWithdrawVariables,
} from '../../../schema-types/UpsertBuilderWithdraw';
import {
  UpsertBuilderTransfer,
  UpsertBuilderTransferVariables,
} from '../../../schema-types/UpsertBuilderTransfer';
import {
  UpsertBuilderAction,
  UpsertBuilderActionVariables,
} from '../../../schema-types/UpsertBuilderAction';
import {
  UpsertBuilderChargeAdHocFee,
  UpsertBuilderChargeAdHocFeeVariables,
} from '../../../schema-types/UpsertBuilderChargeAdHocFee';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {updateRecsVarsProps} from './builderHelpers';
import {saveNew} from './saveNew';
import {saveProtection} from './saveProtection';
import {saveAnnuity} from './saveAnnuity';
import {saveTransfer} from './saveTransfer';
import {saveContribution} from './saveContribution';
import {saveWithdrawal} from './saveWithdrawal';
import {saveAction} from './saveAction';
import {saveModify} from './saveModify';
import {saveAdHocFee} from './saveAdHocFee';

export interface SaveBuilderMutations {
  upsertBuilder: (x: {
    variables: UpsertOvbuilderVariables;
  }) => Promise<FetchResult<UpsertOvbuilder>>;
  upsertSetUpNew: (x: {
    variables: UpsertSetUpNewVariables;
  }) => Promise<FetchResult<UpsertSetUpNew>>;
  upsertBuilderModify: (x: {
    variables: UpsertBuilderModifyVariables;
  }) => Promise<FetchResult<UpsertBuilderModify>>;
  upsertBuilderProtection: (x: {
    variables: UpsertBuilderProtectionVariables;
  }) => Promise<FetchResult<UpsertBuilderProtection>>;
  upsertBuilderAnnuity: (x: {
    variables: UpsertBuilderAnnuityVariables;
  }) => Promise<FetchResult<UpsertBuilderAnnuity>>;
  upsertBuilderContribute: (x: {
    variables: UpsertBuilderContributeVariables;
  }) => Promise<FetchResult<UpsertBuilderContribute>>;
  upsertBuilderWithdraw: (x: {
    variables: UpsertBuilderWithdrawVariables;
  }) => Promise<FetchResult<UpsertBuilderWithdraw>>;
  upsertBuilderTransfer: (x: {
    variables: UpsertBuilderTransferVariables;
  }) => Promise<FetchResult<UpsertBuilderTransfer>>;
  upsertBuilderAction: (x: {
    variables: UpsertBuilderActionVariables;
  }) => Promise<FetchResult<UpsertBuilderAction>>;
  upsertBuilderChargeAdHocFee: (x: {
    variables: UpsertBuilderChargeAdHocFeeVariables;
  }) => Promise<FetchResult<UpsertBuilderChargeAdHocFee>>;
}

export interface SaveBuilderArgs {
  // context-derived
  accountId: string;
  contacts: AccountBasicFragment_contacts[];
  builderDetails: BuilderDetails | null;
  reviewId: string;
  setOvBuilderId: (id: string) => void;
  navigate: NavigateFunction;
  enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey;
  closeSnackbar: (key?: SnackbarKey) => void;
  setSaving: (val: boolean) => void;
  type: builderFieldsType['how'];
  mutations: SaveBuilderMutations;
  // per-invocation
  values: builderFieldsType;
  contactName: string;
  sourcePots: BuilderPot[];
  destinationPots: BuilderPot[];
}

export type createBuilderUpdateProps = Pick<
  SaveBuilderArgs,
  'values' | 'contactName' | 'sourcePots' | 'destinationPots'
>;

type id = string;
const updateBuilder = async (props: updateRecsVarsProps): Promise<id> => {
  const {values} = props;

  switch (values?.planAction) {
    case OVRecommendActionEnum.WITHDRAW:
      return saveWithdrawal(props);
    case OVRecommendActionEnum.CONTRIBUTE:
      return saveContribution(props);
    case OVRecommendActionEnum.TRANSFER:
      return saveTransfer(props);
    case OVRecommendActionEnum.SET_UP_NEW:
      if (values.mainPlanType === 'protectionPlan') {
        return saveProtection(props);
      }
      if (values.mainPlanType === 'annuity') {
        return saveAnnuity(props);
      }
      return saveNew(props);
    case OVRecommendActionEnum.ACTION:
      return saveAction(props);
    case OVRecommendActionEnum.MODIFY:
      return saveModify(props);
    case OVRecommendActionEnum.AD_HOC_FEE:
      return saveAdHocFee(props);
    default:
      throw Error('selected plan action not valid');
  }
};

export async function saveBuilder(args: SaveBuilderArgs): Promise<void> {
  const {
    accountId,
    contacts,
    builderDetails,
    reviewId,
    setOvBuilderId,
    navigate,
    enqueueSnackbar,
    closeSnackbar,
    setSaving,
    type,
    mutations,
    values,
    contactName,
    sourcePots,
    destinationPots,
  } = args;

  const loadingSnackbar = enqueueSnackbar('Saving...', {
    variant: 'info',
    persist: true,
    action: (snackbarId) => (
      <Button
        type="button"
        onClick={() => {
          closeSnackbar(snackbarId);
          setSaving(false);
        }}
      >
        Cancel
      </Button>
    ),
  });

  try {
    const ovrecommendId = await updateBuilder({
      ovrecommendId: builderDetails?.id,
      draft: false,
      accountId,
      values,
      ...mutations,
      contactName,
      builderDetails,
      sourcePots,
      destinationPots,
      enqueueSnackbar,
      reviewId,
      contacts,
    });
    navigate({pathname: '../requirements-builder', search: `previous=${ovrecommendId}`});
    enqueueSnackbar(`${type} ${values.id ? 'updated' : 'added'}`, {variant: 'success'});
  } catch (err: unknown) {
    const existingId = builderDetails?.id;
    if (existingId) {
      setOvBuilderId(existingId);
      window.history.replaceState(
        null,
        document.title,
        `${window.location.pathname}?ovrecommendId=${existingId}`
      );
    }
    console.error(err);
    if (err instanceof Error) {
      const errorFormatted = err.message
        .replace(
          /Contact with id: (.+) doesn't exist/,
          'Contact is not set up with the correct business unit'
        )
        .replace(/pot/i, 'plan');
      enqueueSnackbar(`Error saving this ${values.how}. Error: ${errorFormatted}`, {
        variant: 'error',
        autoHideDuration: null,
      });
    }
    setSaving(false);
  } finally {
    closeSnackbar(loadingSnackbar);
  }
}
